/**
 * CURADORIA VISUAL — HOME OFFICE PREMIUM 2026
 *
 * Fonte: "HomeOfficePremium_Curadoria_Visual_7_Catalogos_2026.pdf" (caderno de curadoria)
 * + códigos confirmados pelo cliente no briefing de curadoria.
 *
 * REGRAS DESTE ARQUIVO
 * - Só entram modelos com fabricante, linha e página de origem identificados.
 * - Nenhuma certificação, dimensão, garantia ou desempenho acústico é inventado.
 * - `notaValidacao` marca o que ainda precisa de confirmação comercial/ficha técnica.
 * - `imagem` é ILUSTRATIVA (render editorial gerado). Não é foto oficial do fabricante
 *   e não representa obra executada pela Home Office Premium. Trocar por foto oficial
 *   quando a autorização de uso dos sete catálogos originais estiver liberada.
 */

export type ClassificacaoUso = 'intensivo' | 'moderado' | 'residencial';

export interface ProdutoCuradoria {
  nome: string;
  fabricante: string;
  linha?: string;
  referencia?: string;
  paginaPdf: string;
  aplicacao: string;
  uso?: ClassificacaoUso;
  imagem: string;
  notaValidacao?: string;
}

export interface FamiliaCatalogo {
  id: string;
  nome: string;
  descricao: string;
  path: string;
  imagem: string;
  fabricantes: string[];
  linhas: string;
}

/** As 12 famílias comerciais do catálogo visual público. */
export const familias: FamiliaCatalogo[] = [
  {
    id: 'cadeiras-corporativas',
    nome: 'Cadeiras corporativas',
    descricao: 'Ergonômicas, operacionais, executivas e giratórias para jornadas longas.',
    path: '/cadeiras',
    imagem:
      '/media/ia/0143.webp',
    fabricantes: ['Plaxmetal'],
    linhas: 'Bix X+, Darix X+, Guick, linha de direção e colaborativa',
  },
  {
    id: 'mesas-executivas',
    nome: 'Mesas executivas',
    descricao: 'Mesas diretivas, gaveteiros e composições com armários.',
    path: '/mesas',
    imagem:
      '/media/ia/0144.webp',
    fabricantes: ['Marzo Vitorino'],
    linhas: 'Altis, Térios',
  },
  {
    id: 'mesas-reuniao',
    nome: 'Mesas de reunião',
    descricao: 'Salas de reunião, conselhos e alinhamentos com privacidade.',
    path: '/mesas',
    imagem:
      '/media/ia/0145.webp',
    fabricantes: ['Marzo Vitorino'],
    linhas: 'Térios, Vio',
  },
  {
    id: 'estacoes-de-trabalho',
    nome: 'Estações de trabalho',
    descricao: 'Plataformas operacionais, bancadas e estações compartilhadas.',
    path: '/mesas',
    imagem:
      '/media/ia/0146.webp',
    fabricantes: ['Marzo Vitorino'],
    linhas: 'Vio',
  },
  {
    id: 'longarinas-recepcao',
    nome: 'Longarinas e recepção',
    descricao: 'Espera, recepção e áreas de permanência.',
    path: '/cadeiras',
    imagem:
      '/media/ia/0147.webp',
    fabricantes: ['Plaxmetal'],
    linhas: 'Elyze, Rizoma',
  },
  {
    id: 'auditorios',
    nome: 'Auditórios e ambientes coletivos',
    descricao: 'Poltronas e assentos para auditórios, escolas e instituições.',
    path: '/escolar',
    imagem:
      '/media/ia/0148.webp',
    fabricantes: ['Plaxmetal'],
    linhas: 'Esibire (confirmar modelo)',
  },
  {
    id: 'divisorias',
    nome: 'Divisórias corporativas',
    descricao: 'Vidro simples, duplo, misto, portas e junta seca.',
    path: '/divisorias',
    imagem:
      '/media/ia/0149.webp',
    fabricantes: ['Gabbinetto'],
    linhas: 'Vidro simples, vidro duplo, mista, junta seca',
  },
  {
    id: 'cabines-acusticas',
    nome: 'Cabines acústicas',
    descricao: 'Espaços de foco, chamadas e reuniões curtas.',
    path: '/cabines-acusticas',
    imagem:
      '/media/ia/0150.webp',
    fabricantes: ['Gabbinetto'],
    linhas: 'Booth',
  },
  {
    id: 'acustica',
    nome: 'Soluções acústicas',
    descricao: 'Painéis, nuvens e tratamento de paredes e forro.',
    path: '/cabines-acusticas',
    imagem:
      '/media/ia/0151.webp',
    fabricantes: ['Gabbinetto'],
    linhas: 'Painéis decorativos, nuvens, forro',
  },
  {
    id: 'mobiliario-urbano',
    nome: 'Mobiliário urbano',
    descricao: 'Bancos e assentos para praças, parques e áreas coletivas.',
    path: '/mobiliario-urbano',
    imagem:
      '/media/ia/0152.webp',
    fabricantes: ['Artesian'],
    linhas: 'Bancos Outdoor, Ripado, Reto, HD, Vox, Curves',
  },
  {
    id: 'pracas-alimentacao',
    nome: 'Praças de alimentação',
    descricao: 'Cadeiras, banquetas, mesas, booths e complementos.',
    path: '/mobiliario-pracas-alimentacao',
    imagem:
      '/media/ia/0153.webp',
    fabricantes: ['Artesian'],
    linhas: 'Asa, Lapa, Forma, Angel, Slice, Mitte',
  },
  {
    id: 'convivencia',
    nome: 'Convivência e áreas comuns',
    descricao: 'Assentos modulares, lounges e espaços de permanência.',
    path: '/mobiliario-urbano',
    imagem:
      '/media/ia/0154.webp',
    fabricantes: ['Plaxmetal', 'Artesian'],
    linhas: 'Rizoma, bancos e assentos coletivos',
  },
];

/* ------------------------------------------------------------------ */
/* ARTESIAN — mobiliário urbano (PDF p.22)                             */
/* ------------------------------------------------------------------ */

export const bancosUrbanos: ProdutoCuradoria[] = [
  {
    nome: 'Banco Outdoor 1',
    fabricante: 'Artesian',
    referencia: 'BCO9357',
    paginaPdf: 'Artesian 2026 — p.22',
    aplicacao: 'Áreas coletivas, praças e espaços de permanência.',
    imagem:
      '/media/ia/0155.webp',
    notaValidacao: 'Confirmar indicação de uso externo, acabamento e fixação na ficha técnica.',
  },
  {
    nome: 'Banco Ripado',
    fabricante: 'Artesian',
    referencia: 'BCO9443',
    paginaPdf: 'Artesian 2026 — p.22',
    aplicacao: 'Praças, jardins e áreas de convivência.',
    imagem:
      '/media/ia/0156.webp',
    notaValidacao: 'Confirmar uso externo e manutenção recomendada.',
  },
  {
    nome: 'Banco Reto',
    fabricante: 'Artesian',
    referencia: 'BCO9447',
    paginaPdf: 'Artesian 2026 — p.22',
    aplicacao: 'Áreas de circulação e convivência coletiva.',
    imagem:
      '/media/ia/0157.webp',
    notaValidacao: 'Confirmar disponibilidade comercial e acabamentos.',
  },
  {
    nome: 'Banco HD',
    fabricante: 'Artesian',
    referencia: 'BCO9453',
    paginaPdf: 'Artesian 2026 — p.22',
    aplicacao: 'Espaços coletivos com uso frequente.',
    imagem:
      '/media/ia/0158.webp',
    notaValidacao: 'Confirmar classificação de uso intensivo na ficha técnica.',
  },
  {
    nome: 'Banco Vox',
    fabricante: 'Artesian',
    referencia: 'BCO9476',
    paginaPdf: 'Artesian 2026 — p.22',
    aplicacao: 'Praças, parques e áreas comuns.',
    imagem:
      '/media/ia/0159.webp',
    notaValidacao: 'Confirmar uso externo e opções de acabamento.',
  },
  {
    nome: 'Banco Curves',
    fabricante: 'Artesian',
    referencia: 'BCO9351',
    paginaPdf: 'Artesian 2026 — p.22',
    aplicacao: 'Jardins, parques e composições curvas.',
    imagem:
      '/media/ia/0160.webp',
    notaValidacao: 'Confirmar raio, módulos e indicação de uso externo.',
  },
];

/** Modelos citados na mesma página do catálogo, com disponibilidade a confirmar. */
export const bancosUrbanosComplementares: ProdutoCuradoria[] = [
  {
    nome: 'Banco Outdoor 2',
    fabricante: 'Artesian',
    referencia: 'BCO9360',
    paginaPdf: 'Artesian 2026 — p.22',
    aplicacao: 'Áreas coletivas externas.',
    imagem: '',
    notaValidacao: 'Confirmar uso externo, acabamento e disponibilidade.',
  },
  {
    nome: 'Banco Vox com Encosto',
    fabricante: 'Artesian',
    referencia: 'BCO9477',
    paginaPdf: 'Artesian 2026 — p.22',
    aplicacao: 'Convivência com maior apoio dorsal.',
    imagem: '',
    notaValidacao: 'Código confirmado pelo cliente; validar com o fabricante.',
  },
  {
    nome: 'Banco Ripado com Encosto',
    fabricante: 'Artesian',
    referencia: 'BCO9418',
    paginaPdf: 'Artesian 2026 — p.22',
    aplicacao: 'Praças e jardins com permanência prolongada.',
    imagem: '',
    notaValidacao: 'Confirmar uso externo e disponibilidade.',
  },
];

/* ------------------------------------------------------------------ */
/* ARTESIAN — praças de alimentação                                    */
/* ------------------------------------------------------------------ */

export const pracaAlimentacao: ProdutoCuradoria[] = [
  {
    nome: 'Cadeira Asa',
    fabricante: 'Artesian',
    paginaPdf: 'Artesian 2026 — p.3-5',
    aplicacao: 'Praças de alimentação e áreas de uso intensivo.',
    uso: 'intensivo',
    imagem:
      '/media/ia/0161.webp',
    notaValidacao: 'Confirmar cor, acabamento e resistência conforme ficha técnica.',
  },
  {
    nome: 'Cadeira Lapa',
    fabricante: 'Artesian',
    paginaPdf: 'Artesian 2026 — p.3-5',
    aplicacao: 'Refeitórios, praças e ambientes coletivos.',
    uso: 'intensivo',
    imagem:
      '/media/ia/0162.webp',
    notaValidacao: 'Confirmar classificação de uso e opções de estrutura.',
  },
  {
    nome: 'Cadeira Forma',
    fabricante: 'Artesian',
    paginaPdf: 'Artesian 2026 — p.3-5',
    aplicacao: 'Ambientes gastronômicos e áreas de convivência.',
    uso: 'intensivo',
    imagem:
      '/media/ia/0163.webp',
    notaValidacao: 'Confirmar cores e disponibilidade comercial.',
  },
  {
    nome: 'Cadeira Mitte',
    fabricante: 'Artesian',
    paginaPdf: 'Artesian 2026 — p.3-5',
    aplicacao: 'Espaços coletivos e operações de alimentação.',
    uso: 'intensivo',
    imagem:
      '/media/ia/0164.webp',
    notaValidacao: 'Confirmar uso intensivo antes de publicar essa afirmação.',
  },
  {
    nome: 'Banquetas para balcão',
    fabricante: 'Artesian',
    paginaPdf: 'Artesian 2026 — p.12-16',
    aplicacao: 'Balcões, bares, ilhas de alimentação e áreas coletivas.',
    imagem:
      '/media/ia/0165.webp',
    notaValidacao: 'Confirmar altura, modelo e linha na ficha técnica.',
  },
  {
    nome: 'Bases e mesas de refeição',
    fabricante: 'Artesian',
    paginaPdf: 'Artesian 2026 — p.19-21',
    aplicacao: 'Praças de alimentação, refeitórios e áreas de descanso.',
    imagem:
      '/media/ia/0166.webp',
    notaValidacao: 'Confirmar medidas, tampo e base conforme catálogo.',
  },
  {
    nome: 'Booth para alimentação',
    fabricante: 'Artesian',
    paginaPdf: 'Artesian 2026 — p.25-26',
    aplicacao: 'Restaurantes, praças e operações gastronômicas.',
    imagem:
      '/media/ia/0167.webp',
    notaValidacao: 'Confirmar modelos e configurações disponíveis.',
  },
  {
    nome: 'Lixeiras e apoios de praça',
    fabricante: 'Artesian',
    paginaPdf: 'Artesian 2026 — p.27-28',
    aplicacao: 'Estações de descarte, porta-bandejas e complementos.',
    imagem:
      '/media/ia/0168.webp',
    notaValidacao: 'Confirmar modelos, capacidade e itens disponíveis.',
  },
];

/* ------------------------------------------------------------------ */
/* MARZO VITORINO — executivo e operação                               */
/* ------------------------------------------------------------------ */

export const corporativo: ProdutoCuradoria[] = [
  {
    nome: 'Mesa diretiva Altis',
    fabricante: 'Marzo Vitorino',
    linha: 'Altis',
    paginaPdf: 'Altis/Térios — p.4 e p.18-20',
    aplicacao: 'Diretorias, gabinetes administrativos e escritórios jurídicos.',
    imagem:
      '/media/ia/0169.webp',
    notaValidacao: 'Confirmar medidas, módulos e acabamentos no catálogo original.',
  },
  {
    nome: 'Conjunto executivo Altis',
    fabricante: 'Marzo Vitorino',
    linha: 'Altis',
    paginaPdf: 'Altis/Térios — p.4',
    aplicacao: 'Gabinete completo com mesa e armários no mesmo padrão.',
    imagem:
      '/media/ia/0170.webp',
    notaValidacao: 'Confirmar composição exata e disponibilidade de cada módulo.',
  },
  {
    nome: 'Sala de reunião Térios',
    fabricante: 'Marzo Vitorino',
    linha: 'Térios',
    paginaPdf: 'Altis/Térios — p.6-9',
    aplicacao: 'Reuniões, conselhos e alinhamentos de diretoria.',
    imagem:
      '/media/ia/0171.webp',
    notaValidacao: 'Confirmar configurações de mesa e lotação.',
  },
  {
    nome: 'Estações de trabalho Vio',
    fabricante: 'Marzo Vitorino',
    linha: 'Vio',
    paginaPdf: 'Vio — p.3',
    aplicacao: 'Empresas em expansão, tecnologia e áreas administrativas.',
    imagem:
      '/media/ia/0172.webp',
    notaValidacao: 'Confirmar módulos, medidas e complementos.',
  },
  {
    nome: 'Mesa individual Vio',
    fabricante: 'Marzo Vitorino',
    linha: 'Vio',
    paginaPdf: 'Vio — p.4 e p.22-29',
    aplicacao: 'Postos individuais e escritórios administrativos.',
    imagem:
      '/media/ia/0173.webp',
    notaValidacao: 'Confirmar gaveteiros e armários compatíveis.',
  },
  {
    nome: 'Sala de reunião Vio',
    fabricante: 'Marzo Vitorino',
    linha: 'Vio',
    paginaPdf: 'Vio — p.5 e p.11-20',
    aplicacao: 'Reuniões contemporâneas e salas de projeto.',
    imagem:
      '/media/ia/0174.webp',
    notaValidacao: 'Confirmar formato de tampo e dimensões.',
  },
];

/* ------------------------------------------------------------------ */
/* PLAXMETAL — cadeiras                                                */
/* ------------------------------------------------------------------ */

export const cadeiras: ProdutoCuradoria[] = [
  {
    nome: 'Cadeira Bix X+',
    fabricante: 'Plaxmetal',
    linha: 'Bix X+',
    paginaPdf: 'Plaxmetal 2026 — p.7',
    aplicacao: 'Escritório, uso prolongado e operação administrativa.',
    imagem:
      '/media/ia/0175.webp',
    notaValidacao: 'Confirmar certificações e mecanismos na ficha técnica do fabricante.',
  },
  {
    nome: 'Cadeira Darix X+',
    fabricante: 'Plaxmetal',
    linha: 'Darix X+',
    paginaPdf: 'Plaxmetal 2026 — p.31',
    aplicacao: 'Ergonomia para jornadas profissionais longas.',
    imagem:
      '/media/ia/0176.webp',
    notaValidacao: 'Confirmar regulagens e disponibilidade de acabamentos.',
  },
  {
    nome: 'Cadeira colaborativa',
    fabricante: 'Plaxmetal',
    paginaPdf: 'Plaxmetal 2026 — p.73',
    aplicacao: 'Salas colaborativas, estações compartilhadas e projetos.',
    imagem:
      '/media/ia/0177.webp',
    notaValidacao: 'Nome da linha a confirmar no catálogo original (p.73).',
  },
  {
    nome: 'Cadeira de direção',
    fabricante: 'Plaxmetal',
    paginaPdf: 'Plaxmetal 2026 — p.106',
    aplicacao: 'Executivo, escritório e liderança.',
    imagem:
      '/media/ia/0178.webp',
    notaValidacao: 'Nome da linha a confirmar no catálogo original (p.106).',
  },
  {
    nome: 'Poltrona Elyze',
    fabricante: 'Plaxmetal',
    linha: 'Elyze',
    paginaPdf: 'Plaxmetal 2026 — p.211',
    aplicacao: 'Recepção, espera e áreas de apoio.',
    imagem:
      '/media/ia/0179.webp',
    notaValidacao: 'Confirmar configurações de braço e tecido.',
  },
  {
    nome: 'Assento modular Rizoma',
    fabricante: 'Plaxmetal',
    linha: 'Rizoma',
    paginaPdf: 'Plaxmetal 2026 — p.196',
    aplicacao: 'Recepção, lounge e espaços coletivos de convivência.',
    imagem:
      '/media/ia/0180.webp',
    notaValidacao: 'Confirmar módulos e combinações disponíveis.',
  },
];

/* ------------------------------------------------------------------ */
/* GABBINETTO — divisórias, cabines e acústica                         */
/* ------------------------------------------------------------------ */

export const divisoriasAcustica: ProdutoCuradoria[] = [
  {
    nome: 'Divisória Vidro Simples',
    fabricante: 'Gabbinetto',
    paginaPdf: 'Gabbinetto Divisórias — p.4',
    aplicacao: 'Escritórios com transparência e separação de ambientes.',
    imagem:
      '/media/ia/0181.webp',
    notaValidacao: 'Confirmar espessura, tipo de vidro e ferragens.',
  },
  {
    nome: 'Divisória Vidro Duplo',
    fabricante: 'Gabbinetto',
    paginaPdf: 'Gabbinetto Divisórias — p.6',
    aplicacao: 'Salas de reunião e ambientes que exigem mais privacidade.',
    imagem:
      '/media/ia/0182.webp',
    notaValidacao: 'Confirmar sistema de persiana e acionamento.',
  },
  {
    nome: 'Divisória Mista',
    fabricante: 'Gabbinetto',
    paginaPdf: 'Gabbinetto Divisórias — p.8',
    aplicacao: 'Composições com madeira e vidro em ambientes administrativos.',
    imagem:
      '/media/ia/0183.webp',
    notaValidacao: 'Confirmar módulos e combinações de acabamento.',
  },
  {
    nome: 'Portas e painéis de vidro',
    fabricante: 'Gabbinetto',
    paginaPdf: 'Gabbinetto Divisórias — p.12',
    aplicacao: 'Acesso a salas de reunião e ambientes corporativos.',
    imagem:
      '/media/ia/0184.webp',
    notaValidacao: 'Confirmar ferragens, fechadura e conformidade de segurança.',
  },
  {
    nome: 'Sistema Junta Seca',
    fabricante: 'Gabbinetto',
    paginaPdf: 'Gabbinetto Divisórias — p.16',
    aplicacao: 'Visual limpo e transparência em escritórios planejados.',
    imagem:
      '/media/ia/0185.webp',
    notaValidacao: 'Confirmar sistema de vedação e aplicação.',
  },
  {
    nome: 'Cabine Booth',
    fabricante: 'Gabbinetto',
    paginaPdf: 'Gabbinetto Booth — p.1, 5, 6 e 8',
    aplicacao: 'Foco, chamadas e reuniões curtas em ambiente compartilhado.',
    imagem:
      '/media/ia/0186.webp',
    notaValidacao: 'Não divulgar índice de atenuação sem ficha técnica do fabricante.',
  },
  {
    nome: 'Painel acústico decorativo',
    fabricante: 'Gabbinetto',
    paginaPdf: 'Gabbinetto Soluções Acústicas — p.3',
    aplicacao: 'Recepções, salas de espera e ambientes de concentração.',
    imagem:
      '/media/ia/0187.webp',
    notaValidacao: 'Confirmar modelos, cores e desempenho conforme ficha técnica.',
  },
  {
    nome: 'Nuvens acústicas',
    fabricante: 'Gabbinetto',
    paginaPdf: 'Gabbinetto Soluções Acústicas — p.4',
    aplicacao: 'Salas de reunião e ambientes com tratamento de teto.',
    imagem:
      '/media/ia/0188.webp',
    notaValidacao: 'Confirmar dimensões, fixação e desempenho acústico.',
  },
];

/** Aviso padrão usado em todas as páginas com imagens dos catálogos. */
export const avisoImagens =
  'Imagens ilustrativas produzidas para esta apresentação. As fotografias oficiais dos fabricantes serão aplicadas após confirmação de autorização de uso, com identificação de linha e modelo.';

/** Aviso que separa produto de obra executada. */
export const avisoProjetos =
  'Produtos e ambientes de catálogo não são obras executadas pela Home Office Premium. A galeria de projetos realizados reúne apenas fornecimentos e instalações próprias.';